<template>
  <div class="portal-acesso-container" data-testid="cliente-acesso-portal">

    <!-- ─────────────────────────────────────────────────────────────
         1. Faixa de estado — informação antes de ação.
         A tarja lateral repete o estado em FORMA, não só em cor, para
         quem escaneia a tela rápido no balcão.
         ───────────────────────────────────────────────────────────── -->
    <div class="portal-status" :class="`is-${severity}`">
      <div class="portal-status-stripe" aria-hidden="true"></div>
      <div class="portal-status-body">

        <div class="portal-status-head">
          <span class="portal-badge" :class="`is-${severity}`" data-testid="cliente-acesso-portal-badge">
            <i :class="badgeIcon" aria-hidden="true"></i>
            {{ badgeLabel }}
          </span>

          <!-- Cada estado mantém o seu testid histórico: o texto ao lado do
               badge é o mesmo fato que antes vivia num <span> solto. -->
          <span v-if="state === 'loading'" class="portal-status-note" data-testid="cliente-acesso-portal-carregando">
            {{ t('Verificando acesso ao portal...') }}
          </span>
          <span v-else-if="state === 'pending_activation'" class="portal-status-note">
            <span v-if="account?.credentialExpiresAt" data-testid="cliente-acesso-portal-expira">
              {{ t('Código válido até') }} {{ formatDateTime(account.credentialExpiresAt) }}
            </span>
          </span>
          <span v-else-if="state === 'active'" class="portal-status-note" data-testid="cliente-acesso-portal-ativo">
            {{ t('Acesso ativo') }}
            <template v-if="account?.lastSeenAt">
              {{ t('— último acesso em') }} {{ formatDateTime(account.lastSeenAt) }}
            </template>
          </span>
          <span v-else-if="state === 'blocked'" class="portal-status-note" data-testid="cliente-acesso-portal-bloqueado">
            {{ t('Acesso bloqueado') }}
            <template v-if="account?.blockedUntil">
              {{ t('até') }} {{ formatDateTime(account.blockedUntil) }}
            </template>
          </span>
          <span v-else-if="state === 'disabled'" class="portal-status-note" data-testid="cliente-acesso-portal-desativado">
            {{ t('Acesso desativado') }}
          </span>
          <span v-else-if="state === 'other'" class="portal-status-note" data-testid="cliente-acesso-portal-outro">
            {{ t('Acesso ao portal:') }} {{ account?.status }}
          </span>
        </div>

        <!-- Datas que o contrato JÁ devolve e que a tela antiga não mostrava. -->
        <dl v-if="temFatos" class="portal-facts">
          <div v-if="account?.createdAt" class="portal-fact">
            <dt>{{ t('Acesso criado em') }}</dt>
            <dd data-testid="cliente-acesso-portal-criado-em">{{ formatDateTime(account.createdAt) }}</dd>
          </div>
          <div v-if="account?.activatedAt" class="portal-fact">
            <dt>{{ t('Ativado em') }}</dt>
            <dd data-testid="cliente-acesso-portal-ativado-em">{{ formatDateTime(account.activatedAt) }}</dd>
          </div>
          <div v-if="account?.lastSeenAt" class="portal-fact">
            <dt>{{ t('Último acesso') }}</dt>
            <dd data-testid="cliente-acesso-portal-ultimo-acesso">{{ formatDateTime(account.lastSeenAt) }}</dd>
          </div>
        </dl>

        <span
          v-if="reachabilityMessage"
          class="portal-status-reach"
          data-testid="cliente-acesso-portal-alcance">
          {{ reachabilityMessage }}
        </span>
      </div>
    </div>

    <!-- Erro e credencial permanecem acima das ações. -->
    <InlineMessage
      v-if="failureMessage"
      severity="error"
      :closable="false"
      data-testid="cliente-acesso-portal-erro">
      {{ failureMessage }}
    </InlineMessage>

    <!-- Só existe logo após a criação: a consulta nunca devolve o código. -->
    <InlineMessage
      v-if="credential"
      severity="success"
      :closable="false"
      data-testid="cliente-acesso-portal-credencial">
      {{ t('Código de ativação para ler ao cliente:') }}
      <strong data-testid="cliente-acesso-portal-codigo">{{ credential.code }}</strong>
      <template v-if="credential.expiresAt">
        {{ t('— válido até') }} {{ formatDateTime(credential.expiresAt) }}
      </template>
    </InlineMessage>

    <!-- ─────────────────────────────────────────────────────────────
         2. UMA ação primária por estado + menu para o resto.
         ───────────────────────────────────────────────────────────── -->
    <div v-if="state !== 'loading'" class="portal-actions">

      <Button
        v-if="state === 'none'"
        :label="t('Criar acesso')"
        icon="pi pi-user-plus"
        :loading="isCreating"
        :disabled="isCreating"
        data-testid="cliente-criar-acesso-portal-btn"
        @click="emit('create')"
      />

      <Button
        v-else-if="state === 'error'"
        :label="t('Tentar de novo')"
        icon="pi pi-refresh"
        severity="secondary"
        :loading="isLoading"
        data-testid="cliente-acesso-portal-retry-btn"
        @click="emit('retry')"
      />

      <!-- Aguardando ativação: a ação útil é emitir um código novo, não um
           botão morto. NÃO se chama "reenviar convite" — nada é enviado: o
           código é lido ao cliente no balcão, e a ação ainda desconecta os
           aparelhos dele (ver o texto da confirmação). O testid do antigo
           botão desabilitado segue aqui: mesmo lugar, mesmo estado. -->
      <Button
        v-else-if="state === 'pending_activation'"
        :label="t('Resetar acesso')"
        icon="pi pi-key"
        :loading="isResetting"
        :disabled="isResetting"
        data-testid="cliente-acesso-portal-pendente-btn"
        @click="emit('reset')"
      />

      <Button
        v-else-if="state === 'active'"
        :label="t('Enviar notificação')"
        icon="pi pi-send"
        data-testid="cliente-enviar-notificacao-btn"
        @click="emit('notify')"
      />

      <Button
        v-else-if="state === 'blocked'"
        :label="t('Reativar acesso')"
        icon="pi pi-unlock"
        :loading="isResetting"
        :disabled="isResetting"
        data-testid="cliente-acesso-portal-reativar-btn"
        @click="emit('reset')"
      />

      <Button
        v-else
        :label="t('Enviar notificação')"
        icon="pi pi-send"
        severity="secondary"
        outlined
        data-testid="cliente-enviar-notificacao-btn"
        @click="emit('notify')"
      />

      <!-- "Enviar notificação" fica SEMPRE como botão, nunca escondida no menu:
           o portal guarda a mensagem para quem ainda não tem login, então é
           ação de primeira linha em qualquer estado. Só não se repete quando
           ela já É a primária. -->
      <Button
        v-if="!notificarEhPrimaria"
        :label="t('Enviar notificação')"
        icon="pi pi-send"
        severity="secondary"
        outlined
        data-testid="cliente-enviar-notificacao-btn"
        @click="emit('notify')"
      />

      <!-- Menu "⋯": só aparece quando existe ação secundária. -->
      <div v-if="acoesSecundarias.length" class="portal-more">
        <Button
          icon="pi pi-ellipsis-h"
          severity="secondary"
          text
          :aria-label="t('Mais ações')"
          data-testid="cliente-acesso-portal-mais-btn"
          @click="menuAberto = !menuAberto"
        />
        <div v-if="menuAberto" class="portal-menu" role="menu" data-testid="cliente-acesso-portal-menu">
          <button
            v-for="acao in acoesSecundarias"
            :key="acao.testid"
            type="button"
            role="menuitem"
            class="portal-menu-item"
            :class="{ 'is-danger': acao.perigosa }"
            :data-testid="acao.testid"
            @click="executarAcao(acao)">
            <i :class="acao.icone" aria-hidden="true"></i>
            {{ acao.rotulo }}
          </button>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────────────────────────────────────────
         4. Histórico — mensagens já aceitas pelo portal, mais recente
         no topo. Fonte: tabela local via /clientes/notificacoes-portal.
         ───────────────────────────────────────────────────────────── -->
    <div class="portal-hist" data-testid="cliente-acesso-portal-historico">
      <div class="portal-hist-head">
        <strong>{{ t('Mensagens enviadas') }}</strong>
        <span v-if="historicoTotal > 0" class="portal-hist-count">{{ historicoTotal }}</span>
      </div>

      <div v-if="historicoIsLoading" class="portal-hist-empty" data-testid="cliente-historico-carregando">
        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
      </div>

      <div v-else-if="historicoFailed" class="portal-hist-empty" data-testid="cliente-historico-erro">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <p class="portal-hist-empty-title">{{ t('Não foi possível carregar o histórico') }}</p>
        <p class="portal-hist-empty-text">{{ t('Tente fechar e abrir o cliente de novo.') }}</p>
      </div>

      <template v-else-if="historicoRows.length > 0">
        <div
          v-for="linha in historicoRows"
          :key="linha.cliente_notification_id"
          class="portal-hist-row"
          :data-testid="`cliente-historico-item-${linha.cliente_notification_id}`">
          <div class="portal-hist-row-head">
            <span class="portal-hist-when">{{ formatDateTime(fromApiDateTimeFormat(linha.cliente_notification_created_at)) }}</span>
            <span v-if="linha.sent_by_name" class="portal-hist-who">{{ linha.sent_by_name }}</span>
            <span
              v-if="linha.cliente_notification_warning === 'no_portal_account'"
              class="portal-hist-flag"
              :title="t('O cliente ainda não tinha acesso quando foi enviada — a mensagem aparece assim que ele ativar.')">
              {{ t('guardada') }}
            </span>
          </div>
          <p class="portal-hist-body">{{ linha.cliente_notification_body }}</p>
        </div>

        <div v-if="historicoTotal > historicoPerPage" class="portal-hist-pager">
          <Button
            :label="t('Anterior')"
            severity="secondary"
            text
            size="small"
            :disabled="historicoPage <= 1"
            data-testid="cliente-historico-anterior"
            @click="mudarPagina(historicoPage - 1)"
          />
          <span class="portal-hist-pager-info">{{ historicoPage }} / {{ totalPaginas }}</span>
          <Button
            :label="t('Próxima')"
            severity="secondary"
            text
            size="small"
            :disabled="historicoPage >= totalPaginas"
            data-testid="cliente-historico-proxima"
            @click="mudarPagina(historicoPage + 1)"
          />
        </div>
      </template>

      <div v-else class="portal-hist-empty" data-testid="cliente-historico-vazio">
        <i class="pi pi-inbox" aria-hidden="true"></i>
        <p class="portal-hist-empty-title">{{ t('Nenhuma mensagem enviada ainda') }}</p>
        <p class="portal-hist-empty-text">
          {{ t('Quando alguém enviar um aviso a este cliente, ele aparece aqui — com data, texto e quem enviou.') }}
        </p>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
/**
 * Aba "Portal do cliente" do ClienteModal.
 *
 * APRESENTACIONAL de propósito: quem detém o estado do portal continua sendo o
 * ClienteModal (que já carrega useClienteAcessoPortal num watch gateado pela
 * feature flag). Mantendo os dados lá em cima, o momento da requisição não muda
 * e o teste que conta requests com a flag desligada continua valendo.
 *
 * A EXCEÇÃO à regra apresentacional é o histórico: ele é conteúdo próprio da
 * aba (tabela local, sem chamada ao portal) e só carrega quando a aba monta —
 * o TabPanel é lazy, então nenhum tenant sem a feature gera essa requisição.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useClienteNotificacao } from '../../composables/useClienteNotificacao';

interface PortalAccount {
  status?: string;
  createdAt?: Date | null;
  activatedAt?: Date | null;
  lastSeenAt?: Date | null;
  credentialExpiresAt?: Date | null;
  blockedUntil?: Date | null;
  pushDevices?: number | null;
}

interface PortalCredential {
  code: string;
  expiresAt?: Date | null;
}

interface AcaoSecundaria {
  rotulo: string;
  icone: string;
  testid: string;
  evento: 'reset';
  perigosa?: boolean;
}

const props = withDefaults(defineProps<{
  state: string;
  clienteId: number;
  account?: PortalAccount | null;
  credential?: PortalCredential | null;
  failureMessage?: string;
  reachabilityMessage?: string;
  isLoading?: boolean;
  isCreating?: boolean;
  isResetting?: boolean;
  podeResetar?: boolean;
}>(), {
  account: null,
  credential: null,
  failureMessage: '',
  reachabilityMessage: '',
  isLoading: false,
  isCreating: false,
  isResetting: false,
  podeResetar: false,
});

const emit = defineEmits<{
  create: [];
  retry: [];
  reset: [];
  notify: [];
}>();

const { t } = useI18n();
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter();

// ── Histórico ───────────────────────────────────────────────────────────────
const {
  historicoRows, historicoTotal, historicoPage, historicoPerPage,
  historicoIsLoading, historicoFailed, carregarHistorico,
} = useClienteNotificacao();

const totalPaginas = computed<number>(() =>
  Math.max(1, Math.ceil(historicoTotal.value / historicoPerPage.value))
);

function mudarPagina(pagina: number): void {
  carregarHistorico(props.clienteId, pagina);
}

/** Um envio aceito (pelo modal) entra no topo da lista — volta à página 1. */
function aoEnviar(payload?: { id?: number }): void {
  if (payload?.id === props.clienteId) carregarHistorico(props.clienteId, 1);
}

onMounted(() => {
  carregarHistorico(props.clienteId, 1);
  eventBus.on('cliente-notificacao.enviada', aoEnviar);
});
onBeforeUnmount(() => {
  eventBus.off('cliente-notificacao.enviada', aoEnviar);
});

const menuAberto = ref<boolean>(false);

/** Vocabulário do badge por estado. Um mapa, não uma cadeia de v-if. */
const BADGES: Record<string, { rotulo: string; severidade: string; icone: string }> = {
  loading:            { rotulo: 'Verificando...',   severidade: 'neutro',   icone: 'pi pi-spin pi-spinner' },
  none:               { rotulo: 'Sem acesso',       severidade: 'neutro',   icone: 'pi pi-minus-circle' },
  pending_activation: { rotulo: 'Aguardando ativação', severidade: 'pendente', icone: 'pi pi-clock' },
  active:             { rotulo: 'Ativo',            severidade: 'ok',       icone: 'pi pi-check-circle' },
  blocked:            { rotulo: 'Bloqueado',        severidade: 'perigo',   icone: 'pi pi-ban' },
  disabled:           { rotulo: 'Desativado',       severidade: 'neutro',   icone: 'pi pi-power-off' },
  error:              { rotulo: 'Não foi possível verificar', severidade: 'perigo', icone: 'pi pi-exclamation-triangle' },
  other:              { rotulo: 'Acesso ao portal', severidade: 'neutro',   icone: 'pi pi-info-circle' },
};

const badge = computed(() => BADGES[props.state] ?? BADGES.other);
const badgeLabel = computed<string>(() => t(badge.value.rotulo));
const badgeIcon = computed<string>(() => badge.value.icone);
const severity = computed<string>(() => badge.value.severidade);

const temFatos = computed<boolean>(() =>
  !!(props.account?.createdAt || props.account?.activatedAt || props.account?.lastSeenAt)
);

/**
 * Estados em que "Enviar notificação" JÁ é a ação primária — nos demais ela vai
 * para o menu, mas nunca desaparece: o portal guarda a mensagem para um cliente
 * que ainda não tem login e a mostra assim que o acesso é criado, então tirar a
 * ação daqui derrubaria algo que o atendente acredita ter enviado.
 */
const NOTIFICAR_E_PRIMARIA = ['active', 'disabled', 'other'];

/** Estados em que a primária já dispara o reset (reenviar convite / reativar). */
const RESET_E_PRIMARIO = ['pending_activation', 'blocked'];

/** Evita repetir "Enviar notificação" quando ela já ocupa a posição primária. */
const notificarEhPrimaria = computed<boolean>(
  () => props.state === 'loading' || NOTIFICAR_E_PRIMARIA.includes(props.state)
);

/** O que sobra para o menu — hoje só o reset, quando não é a primária. */
const acoesSecundarias = computed<AcaoSecundaria[]>(() => {
  const acoes: AcaoSecundaria[] = [];
  if (props.state === 'loading') return acoes;

  if (props.podeResetar && !RESET_E_PRIMARIO.includes(props.state)) {
    acoes.push({
      rotulo: t('Resetar acesso'),
      icone: 'pi pi-refresh',
      testid: 'cliente-resetar-acesso-portal-btn',
      evento: 'reset',
      perigosa: true,
    });
  }
  return acoes;
});

function executarAcao(acao: AcaoSecundaria): void {
  menuAberto.value = false;
  emit(acao.evento);
}

</script>

<style scoped>
.portal-acesso-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}

/* ── faixa de estado ───────────────────────────────────────────── */
.portal-status {
  display: grid;
  grid-template-columns: 4px 1fr;
  width: 100%;
  border: 1px solid var(--p-surface-300, #dee2e6);
  border-radius: 8px;
  overflow: hidden;
}
.portal-status-stripe { background: var(--p-surface-400, #ced4da); }
.portal-status.is-ok       .portal-status-stripe { background: var(--p-green-500, #22c55e); }
.portal-status.is-pendente .portal-status-stripe { background: var(--p-yellow-600, #ca9f00); }
.portal-status.is-perigo   .portal-status-stripe { background: var(--p-red-500, #ef4444); }

.portal-status-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.portal-status-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.portal-status-note { font-size: 0.875rem; color: var(--p-text-muted-color, #6c757d); }
.portal-status-reach { font-size: 0.8125rem; color: var(--p-text-muted-color, #6c757d); }

.portal-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 650;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--p-surface-200, #e9ecef);
  color: var(--p-text-color, #343a40);
  white-space: nowrap;
}
.portal-badge.is-ok       { background: #e6f4ec; color: #1c7a4a; }
.portal-badge.is-pendente { background: #f9efd9; color: #8a6110; }
.portal-badge.is-perigo   { background: #fbeae7; color: #b3392b; }

.portal-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px 18px;
  margin: 0;
}
.portal-fact dt {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--p-text-muted-color, #6c757d);
  font-weight: 600;
  margin-bottom: 2px;
}
.portal-fact dd {
  margin: 0;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
}

/* ── ações ─────────────────────────────────────────────────────── */
.portal-actions {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}
/* Os botões dividem a largura do modal; o "⋯" fica no tamanho do ícone. */
.portal-actions > :deep(.p-button) { flex: 1 1 0; justify-content: center; }
.portal-more { position: relative; flex: 0 0 auto; }
.portal-more :deep(.p-button) { flex: 0 0 auto; }
.portal-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  min-width: 200px;
  padding: 4px;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-300, #dee2e6);
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}
.portal-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  font: inherit;
  font-size: 0.875rem;
  padding: 8px 10px;
  background: none;
  border: 0;
  border-radius: 4px;
  color: var(--p-text-color, #343a40);
  cursor: pointer;
}
.portal-menu-item:hover { background: var(--p-surface-100, #f8f9fa); }
.portal-menu-item.is-danger { color: var(--p-red-600, #dc2626); }

/* ── histórico ─────────────────────────────────────────────────── */
.portal-hist {
  width: 100%;
  border: 1px solid var(--p-surface-300, #dee2e6);
  border-radius: 8px;
  overflow: hidden;
}
.portal-hist-head {
  padding: 9px 12px;
  border-bottom: 1px solid var(--p-surface-200, #e9ecef);
  background: var(--p-surface-50, #fafafa);
  font-size: 0.875rem;
}
.portal-hist-count {
  font-size: 0.75rem;
  font-weight: 650;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--p-surface-200, #e9ecef);
  color: var(--p-text-muted-color, #6c757d);
}
.portal-hist-row {
  padding: 10px 12px;
  border-bottom: 1px solid var(--p-surface-200, #e9ecef);
}
.portal-hist-row:last-of-type { border-bottom: 0; }
.portal-hist-row-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.portal-hist-when {
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #6c757d);
  font-variant-numeric: tabular-nums;
}
.portal-hist-who { font-size: 0.78rem; color: var(--p-text-muted-color, #6c757d); }
.portal-hist-flag {
  font-size: 0.6875rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f9efd9;
  color: #8a6110;
}
.portal-hist-body {
  margin: 0;
  font-size: 0.875rem;
  max-width: 70ch;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.portal-hist-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid var(--p-surface-200, #e9ecef);
  background: var(--p-surface-50, #fafafa);
}
.portal-hist-pager-info {
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #6c757d);
  font-variant-numeric: tabular-nums;
}
.portal-hist-empty { padding: 26px 18px; text-align: center; }
.portal-hist-empty i { font-size: 1.4rem; color: var(--p-text-muted-color, #6c757d); }
.portal-hist-empty-title { margin: 8px 0 4px; font-weight: 620; font-size: 0.9375rem; }
.portal-hist-empty-text {
  margin: 0 auto;
  max-width: 46ch;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #6c757d);
}
.portal-hist-empty-note {
  margin: 8px 0 0;
  font-size: 0.75rem;
  font-style: italic;
  color: var(--p-text-muted-color, #6c757d);
}
</style>
